import { trpc } from "../../trpc";
import { Merchant } from "../../types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Edit2, Mail, CheckCircle2, XCircle, Home, Building, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

type Props = {
  merchant: Merchant;
  onEdit: (merchant: Merchant) => void;
  isIncomplete?: boolean;
};

const StatusIndicator = ({ isActive }: { isActive: boolean }) => {
  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
        isActive 
          ? 'bg-green-100 text-green-800 border border-green-200' 
          : 'bg-gray-100 text-gray-600 border border-gray-200'
      }`}
    >
      {isActive ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      <span>{isActive ? 'Activo' : 'Inactivo'}</span>
    </div>
  );
};

const MerchantTypeBadge = ({ type }: { type: string }) => {
  const isRentals = type === "rentals";
  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
        isRentals 
          ? 'bg-purple-100 text-purple-800 border border-purple-200' 
          : 'bg-blue-100 text-blue-800 border border-blue-200'
      }`}
    >
      {isRentals ? (
        <Home className="h-3.5 w-3.5" />
      ) : (
        <Building className="h-3.5 w-3.5" />
      )}
      <span className="capitalize">{type}</span>
    </div>
  );
};

const TemplateStatusBadge = ({ merchantId }: { merchantId: string }) => {
  const templatesQuery = trpc.whatsappTemplates.list.useQuery({ merchantId });

  const autoCreateMutation = trpc.whatsappTemplates.autoCreateEmailTemplates.useMutation({
    onSuccess: (data) => {
      if (data.created.length > 0) {
        toast.success(`Plantillas creadas: ${data.created.join(", ")}`);
      }
      if (data.skipped.length > 0) {
        toast.info(`Plantillas existentes: ${data.skipped.map((s) => s.systemId).join(", ")}`);
      }
      if (data.errors.length > 0) {
        toast.error(`Errores: ${data.errors.map((e) => `${e.systemId}: ${e.error}`).join(", ")}`);
      }
      templatesQuery.refetch();
    },
    onError: (error) => {
      toast.error("Error creando plantillas", { description: error.message });
    },
  });

  const syncMutation = trpc.whatsappTemplates.syncStatuses.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      templatesQuery.refetch();
    },
    onError: (error) => {
      toast.error("Error sincronizando", { description: error.message });
    },
  });

  if (templatesQuery.isLoading) {
    return <Loader2 className="h-4 w-4 animate-spin text-gray-400" />;
  }

  const templates = templatesQuery.data?.templates ?? [];
  const primaryWabaId = templatesQuery.data?.primaryWabaId;
  const outboundTemplates = templates.filter(
    (t) => t.systemId?.startsWith("portals-email-outbound") || t.systemId === "outbound-default"
  );

  const wabaIdDisplay = primaryWabaId ? (
    <p className="text-xs text-gray-400 font-mono select-all" title="WABA ID">
      WABA: {primaryWabaId}
    </p>
  ) : (
    <p className="text-xs text-red-400">Sin WABA configurado</p>
  );

  const allApproved = outboundTemplates.length > 0 && outboundTemplates.every((t) => t.status === "APPROVED");
  const anyDeleted = outboundTemplates.some((t) => t.status === "DELETED");
  const anyRejected = outboundTemplates.some((t) => t.status === "REJECTED");
  const anyPending = outboundTemplates.some((t) => t.status === "PENDING");

  return (
    <div className="space-y-1.5">
      {wabaIdDisplay}
      <div className="flex items-center gap-2">
        {outboundTemplates.length === 0 ? (
          <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">
            Sin plantillas
          </Badge>
        ) : anyDeleted ? (
          <Badge className="bg-red-200 text-red-900 border-red-300 hover:bg-red-200">
            Eliminada
          </Badge>
        ) : allApproved ? (
          <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
            Plantillas activas
          </Badge>
        ) : anyRejected ? (
          <Badge className="bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100">
            Rechazada
          </Badge>
        ) : anyPending ? (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">
            Pendiente aprobacion
          </Badge>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => autoCreateMutation.mutate({ merchantId })}
          disabled={autoCreateMutation.isLoading || !primaryWabaId}
        >
          {autoCreateMutation.isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : null}
          Crear plantillas
        </Button>
        {outboundTemplates.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => syncMutation.mutate({ merchantId })}
            disabled={syncMutation.isLoading}
            title="Sincronizar estados"
          >
            {syncMutation.isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

const MerchantCard = ({ merchant, onEdit, isIncomplete = false }: Props) => {
  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "N/A";
    return value.toLocaleString(undefined, {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return "N/A";
    return value.toLocaleString();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Card className={`overflow-hidden border ${isIncomplete ? 'border-orange-300' : 'border-gray-200'} hover:shadow-md transition-shadow`}>
      <CardContent className="p-0">
        {/* Header Section */}
        <div className={`px-5 py-4 border-b ${isIncomplete ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h3 className="text-lg font-bold text-gray-900">
                  {merchant.id}
                </h3>
                <div className="flex items-center gap-2">
                  <MerchantTypeBadge type={merchant.type} />
                  <StatusIndicator isActive={merchant.currentCustomer ?? false} />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Ciclo de facturación: Día {formatNumber(merchant.billingCycleDay)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(merchant)}
              className="shrink-0"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Editar
            </Button>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Pricing Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Tarifas
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Mínimo:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(merchant.minimumSubscriptionUsage)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Debajo del mín:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(merchant.unitPriceBelowMinimum)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Encima del mín:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(merchant.unitPriceAboveMinimum)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Minuto llamada:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(merchant.unitPricePerCallMinute)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Plantilla saliente:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(merchant.unitPriceOutbound)}</span>
                </div>
              </div>
            </div>

            {/* Invoice Dates Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Facturación
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Primera:</span>
                  <span className="font-semibold text-gray-900">{formatDate(merchant.firstInvoicePaidDate)}</span>
                </div>
                {!merchant.currentCustomer && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Última:</span>
                    <span className="font-semibold text-gray-900">{formatDate(merchant.lastInvoicePaidDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Help Center Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Centro de Ayuda
              </h4>
              {merchant.helpCenterUrl ? (
                <a
                  href={merchant.helpCenterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-words"
                >
                  {merchant.helpCenterUrl}
                </a>
              ) : (
                <p className="text-sm text-gray-400 italic">No configurado</p>
              )}
            </div>

            {/* Email Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Emails Identificadores
              </h4>
              {merchant.type === "rentals" && (
                <div className="mb-2">
                  <TemplateStatusBadge merchantId={merchant.id} />
                </div>
              )}
              {merchant.merchantIdentifierEmail && merchant.merchantIdentifierEmail.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-gray-900 break-all">{merchant.merchantIdentifierEmail[0]}</p>
                  </div>

                  {merchant.merchantIdentifierEmail.length > 1 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          +{merchant.merchantIdentifierEmail.length - 1} más
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-600">Todos los emails</p>
                          {merchant.merchantIdentifierEmail.map((email, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <Mail className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
                              <p className="text-sm text-gray-900 break-all">{email}</p>
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No configurado</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MerchantCard;